import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import usePages, { filterPagesByTeamspace, filterPagesByCollection, hasNoCollection } from './usePages';
import useFavorites from './useFavorites';
import useRecents from './useRecents';
import useCollections from './useCollections';
import TemplatePicker from './TemplatePicker';
import BacklinksPanel from '../editor/BacklinksPanel';
import './nav.css';

const TYPE_EMOJI = { document: '📄', database: '🗂️', db_row: '📄' };

// 공개범위 라벨/배지 (공개/비공개 2종)
const VISIBILITY = {
  open: { label: '공개', short: '공개' },
  private: { label: '비공개', short: '🔒 비공개' },
};
const VIS_ORDER = ['open', 'private'];

// 자주 쓰는 이모지 (아이콘 선택용)
const ICON_CHOICES = ['📁', '📂', '🗂️', '📌', '⭐', '🚀', '💼', '🏢', '🎯', '🧩', '🔧', '🎨', '📊', '💡', '🔥', '🌱'];

// 팀스페이스 생성/편집용 팝오버 (이름 + 아이콘 + 공개범위).
function TeamspaceForm({ initial, title, submitLabel, onSubmit, onCancel }) {
  const [name, setName] = useState(initial?.name || '');
  const [icon, setIcon] = useState(initial?.icon || '📁');
  const [visibility, setVisibility] = useState(initial?.visibility || 'open');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, []);

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    const ok = await onSubmit({ name: trimmed, icon, visibility });
    if (!ok) setBusy(false); // 실패 시 폼 유지
  }

  return (
    <div className="nav-tsform" onClick={(e) => e.stopPropagation()}>
      <div className="nav-tsform-title">{title}</div>
      <div className="nav-tsform-row">
        <span className="nav-tsform-icon">{icon}</span>
        <input
          ref={inputRef}
          className="nav-tsform-input"
          placeholder="팀스페이스 이름"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); submit(); }
            else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          }}
        />
      </div>
      <div className="nav-tsform-icons">
        {ICON_CHOICES.map((ic) => (
          <button
            key={ic}
            type="button"
            className={`nav-tsform-ic${ic === icon ? ' on' : ''}`}
            onClick={() => setIcon(ic)}
          >{ic}</button>
        ))}
      </div>
      <div className="nav-tsform-vis">
        {VIS_ORDER.map((v) => (
          <button
            key={v}
            type="button"
            className={`nav-tsform-visbtn${v === visibility ? ' on' : ''}`}
            onClick={() => setVisibility(v)}
          >{v === 'private' ? '🔒 비공개' : '공개'}</button>
        ))}
      </div>
      <div className="nav-tsform-actions">
        <button type="button" className="nav-tsform-cancel" onClick={onCancel} disabled={busy}>취소</button>
        <button type="button" className="nav-tsform-submit" onClick={submit} disabled={busy || !name.trim()}>
          {busy ? '처리 중…' : submitLabel}
        </button>
      </div>
    </div>
  );
}

// 컬렉션(커스텀 섹션) 생성/편집용 팝오버 폼 (이름 + 아이콘). 팀스페이스 폼과 같은 톤, 공개범위 없음.
function CollectionForm({ initial, title, submitLabel, onSubmit, onCancel }) {
  const [name, setName] = useState(initial?.name || '');
  const [icon, setIcon] = useState(initial?.icon || '📁');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, []);

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    const ok = await onSubmit({ name: trimmed, icon });
    if (!ok) setBusy(false); // 실패 시 폼 유지
  }

  return (
    <div className="nav-tsform" onClick={(e) => e.stopPropagation()}>
      <div className="nav-tsform-title">{title}</div>
      <div className="nav-tsform-row">
        <span className="nav-tsform-icon">{icon}</span>
        <input
          ref={inputRef}
          className="nav-tsform-input"
          placeholder="섹션 이름"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); submit(); }
            else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          }}
        />
      </div>
      <div className="nav-tsform-icons">
        {ICON_CHOICES.map((ic) => (
          <button
            key={ic}
            type="button"
            className={`nav-tsform-ic${ic === icon ? ' on' : ''}`}
            onClick={() => setIcon(ic)}
          >{ic}</button>
        ))}
      </div>
      <div className="nav-tsform-actions">
        <button type="button" className="nav-tsform-cancel" onClick={onCancel} disabled={busy}>취소</button>
        <button type="button" className="nav-tsform-submit" onClick={submit} disabled={busy || !name.trim()}>
          {busy ? '처리 중…' : submitLabel}
        </button>
      </div>
    </div>
  );
}

// parent_id 기준으로 평평한 페이지 배열을 트리(children) 구조로 변환.
function buildTree(pages) {
  const byId = new Map();
  pages.forEach((p) => byId.set(p.id, { ...p, children: [] }));
  const roots = [];
  byId.forEach((node) => {
    if (node.parent_id && byId.has(node.parent_id)) {
      byId.get(node.parent_id).children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortRec = (nodes) => {
    nodes.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

// 인라인 이름변경 input row
function RenameRow({ depth, renameRef, value, onChange, onCommit, onCancel }) {
  return (
    <div className="nav-item" style={{ paddingLeft: 8 + depth * 16 }}>
      <span className="nav-tw spacer" />
      <span className="em">📄</span>
      <input
        ref={renameRef}
        className="nav-rename"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onBlur={onCommit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); onCommit(); }
          else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
      />
    </div>
  );
}

// 페이지 트리 노드 — 재귀 렌더 + 인라인 rename 지원.
function PageNode({
  node, depth, selectedId, expanded, toggle, onOpen, onAddChild, onMenu,
  renaming, renameRef, onRenameChange, onRenameCommit, onRenameCancel,
}) {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.id);
  const isSel = node.id === selectedId;
  // 노션 동기화: 아이콘이 있으면 그대로, 없으면 — 문서는 노션처럼 옅은 기본 문서 아이콘,
  // database/db_row 등은 기존 타입 아이콘 유지(깨짐 방지).
  const emoji = node.icon || TYPE_EMOJI[node.type] || '📄';
  const isFaintIcon = !node.icon && node.type === 'document'; // 아이콘 없는 문서 → 옅게
  const isRenaming = renaming && renaming.id === node.id;

  return (
    <>
      {isRenaming ? (
        <RenameRow
          depth={depth}
          renameRef={renameRef}
          value={renaming.value}
          onChange={onRenameChange}
          onCommit={onRenameCommit}
          onCancel={onRenameCancel}
        />
      ) : (
        <div
          className={`nav-item${isSel ? ' sel' : ''}`}
          style={{ paddingLeft: 8 + depth * 16 }}
          onClick={() => onOpen(node.id)}
        >
          <span
            className={`nav-tw${hasChildren && isOpen ? ' open' : ''}${hasChildren ? '' : ' spacer'}`}
            onClick={(e) => { e.stopPropagation(); if (hasChildren) toggle(node.id); }}
          >
            {hasChildren ? (isOpen ? '▾' : '▸') : '•'}
          </span>
          <span className={`em${isFaintIcon ? ' em-faint' : ''}`}>{emoji}</span>
          <span className="nav-label">{node.title || '제목 없음'}</span>
          <span className="nav-actions">
            <button type="button" className="nav-actbtn" title="더보기"
              onClick={(e) => { e.stopPropagation(); onMenu(node, e.currentTarget); }}>⋯</button>
            <button type="button" className="nav-actbtn" title="하위 페이지 추가"
              onClick={(e) => { e.stopPropagation(); onAddChild(node); }}>＋</button>
          </span>
        </div>
      )}
      {isOpen && hasChildren && (
        <div>
          {node.children.map((c) => (
            <PageNode
              key={c.id}
              node={c}
              depth={depth + 1}
              selectedId={selectedId}
              expanded={expanded}
              toggle={toggle}
              onOpen={onOpen}
              onAddChild={onAddChild}
              onMenu={onMenu}
              renaming={renaming}
              renameRef={renameRef}
              onRenameChange={onRenameChange}
              onRenameCommit={onRenameCommit}
              onRenameCancel={onRenameCancel}
            />
          ))}
        </div>
      )}
    </>
  );
}

// 팀스페이스 섹션 — 이름 클릭 시 펼침/접힘, 그 아래에 해당 팀의 최상위 페이지 트리 표시.
function TeamspaceSection({
  teamspace, tree, isOpen, onToggle, onAddPage, onTsMenu,
  tsRenaming, tsRenameRef, onTsRenameChange, onTsRenameCommit, onTsRenameCancel,
  selectedId, expanded, toggle, onOpen, onAddChild, onMenu,
  renaming, renameRef, onRenameChange, onRenameCommit, onRenameCancel,
}) {
  const isTsRenaming = tsRenaming && tsRenaming.id === teamspace.id;
  return (
    <>
      {isTsRenaming ? (
        <div className="nav-item nav-ts">
          <span className="nav-tw open">{isOpen ? '▾' : '▸'}</span>
          <span className="em">{teamspace.icon || '📁'}</span>
          <input
            ref={tsRenameRef}
            className="nav-rename"
            value={tsRenaming.value}
            onChange={(e) => onTsRenameChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onBlur={onTsRenameCommit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); onTsRenameCommit(); }
              else if (e.key === 'Escape') { e.preventDefault(); onTsRenameCancel(); }
            }}
          />
        </div>
      ) : (
        <div className="nav-item nav-ts" onClick={() => onToggle(teamspace.id)}>
          <span className={`nav-tw${isOpen ? ' open' : ''}`}>{isOpen ? '▾' : '▸'}</span>
          <span className="em">{teamspace.icon || '📁'}</span>
          <span className="nav-label">{teamspace.name}</span>
          {teamspace.visibility === 'private' && (
            <span className="nav-tsbadge private">
              {VISIBILITY.private.short}
            </span>
          )}
          <span className="nav-actions">
            <button type="button" className="nav-actbtn" title="팀스페이스 옵션"
              onClick={(e) => { e.stopPropagation(); onTsMenu(teamspace, e.currentTarget); }}>⋯</button>
            <button type="button" className="nav-actbtn" title="새 페이지"
              onClick={(e) => { e.stopPropagation(); onAddPage(teamspace); }}>＋</button>
          </span>
        </div>
      )}
      {isOpen && (
        <div>
          {tree.length === 0 ? (
            <div className="nav-empty-child" style={{ paddingLeft: 24 }}>페이지 없음</div>
          ) : (
            tree.map((node) => (
              <PageNode
                key={node.id}
                node={node}
                depth={1}
                selectedId={selectedId}
                expanded={expanded}
                toggle={toggle}
                onOpen={onOpen}
                onAddChild={onAddChild}
                onMenu={onMenu}
                renaming={renaming}
                renameRef={renameRef}
                onRenameChange={onRenameChange}
                onRenameCommit={onRenameCommit}
                onRenameCancel={onRenameCancel}
              />
            ))
          )}
        </div>
      )}
    </>
  );
}

// 커스텀 섹션(컬렉션) — 팀스페이스 섹션과 같은 톤. 헤더(아이콘+이름+접기/펼치기+⋯+＋) + 페이지 트리.
function CollectionSection({
  collection, tree, isOpen, onToggle, onAddPage, onColMenu,
  colRenaming, colRenameRef, onColRenameChange, onColRenameCommit, onColRenameCancel,
  selectedId, expanded, toggle, onOpen, onAddChild, onMenu,
  renaming, renameRef, onRenameChange, onRenameCommit, onRenameCancel,
}) {
  const isColRenaming = colRenaming && colRenaming.id === collection.id;
  return (
    <>
      {isColRenaming ? (
        <div className="nav-item nav-ts">
          <span className="nav-tw open">{isOpen ? '▾' : '▸'}</span>
          <span className="em">{collection.icon || '📁'}</span>
          <input
            ref={colRenameRef}
            className="nav-rename"
            value={colRenaming.value}
            onChange={(e) => onColRenameChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onBlur={onColRenameCommit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); onColRenameCommit(); }
              else if (e.key === 'Escape') { e.preventDefault(); onColRenameCancel(); }
            }}
          />
        </div>
      ) : (
        <div className="nav-item nav-ts" onClick={() => onToggle(collection.id)}>
          <span className={`nav-tw${isOpen ? ' open' : ''}`}>{isOpen ? '▾' : '▸'}</span>
          <span className="em">{collection.icon || '📁'}</span>
          <span className="nav-label">{collection.name}</span>
          <span className="nav-actions">
            <button type="button" className="nav-actbtn" title="섹션 옵션"
              onClick={(e) => { e.stopPropagation(); onColMenu(collection, e.currentTarget); }}>⋯</button>
            <button type="button" className="nav-actbtn" title="이 섹션에 새 페이지"
              onClick={(e) => { e.stopPropagation(); onAddPage(collection); }}>＋</button>
          </span>
        </div>
      )}
      {isOpen && (
        <div>
          {tree.length === 0 ? (
            <div className="nav-empty-child" style={{ paddingLeft: 24 }}>페이지 없음</div>
          ) : (
            tree.map((node) => (
              <PageNode
                key={node.id}
                node={node}
                depth={1}
                selectedId={selectedId}
                expanded={expanded}
                toggle={toggle}
                onOpen={onOpen}
                onAddChild={onAddChild}
                onMenu={onMenu}
                renaming={renaming}
                renameRef={renameRef}
                onRenameChange={onRenameChange}
                onRenameCommit={onRenameCommit}
                onRenameCancel={onRenameCancel}
              />
            ))
          )}
        </div>
      )}
    </>
  );
}

// 플랫 페이지 행 (즐겨찾기 / 최근 항목 섹션용) — 트리 없이 한 줄로 표시.
function FlatPageItem({ page, selectedId, onOpen, onMenu }) {
  const emoji = page.icon || TYPE_EMOJI[page.type] || '📄';
  const isFaintIcon = !page.icon && page.type === 'document';
  const isSel = page.id === selectedId;
  return (
    <div
      className={`nav-item${isSel ? ' sel' : ''}`}
      style={{ paddingLeft: 8 }}
      onClick={() => onOpen(page.id)}
    >
      <span className="nav-tw spacer" />
      <span className={`em${isFaintIcon ? ' em-faint' : ''}`}>{emoji}</span>
      <span className="nav-label">{page.title || '제목 없음'}</span>
      <span className="nav-actions">
        <button type="button" className="nav-actbtn" title="더보기"
          onClick={(e) => { e.stopPropagation(); onMenu(page, e.currentTarget); }}>⋯</button>
      </span>
    </div>
  );
}

// 접기/펼치기 가능한 섹션 헤더 (노션 톤의 작은 회색 라벨).
function SectionLabel({ icon, label, open, onToggle }) {
  return (
    <div className="nav-seclabel nav-seclabel-row nav-seclabel-toggle" onClick={onToggle}>
      <span>
        <span className="nav-seclabel-tw">{open ? '▾' : '▸'}</span>
        {icon ? <span className="nav-seclabel-ic">{icon}</span> : null}
        <span>{label}</span>
      </span>
    </div>
  );
}

export default function Sidebar({ onNavigate, mobileOpen = false }) {
  const navigate = useNavigate();
  const { pageId: routeId } = useParams();
  const { user, membership, teamspaces, signOut, refresh } = useAuth();
  const { pages, loading, createPage, updatePage, archivePage } = usePages();
  const { favoriteIds, isFavorite, toggleFavorite } = useFavorites();
  const { recentIds } = useRecents();
  const { collections, createCollection, renameCollection, deleteCollection } = useCollections();
  const myUid = user?.id ?? null;

  const [expanded, setExpanded] = useState(() => new Set());
  const [tsOpen, setTsOpen] = useState(() => new Set()); // 펼쳐진 팀스페이스 id
  const [colOpen, setColOpen] = useState(() => new Set()); // 펼쳐진 컬렉션 id
  // 섹션 접기/펼치기 상태 (기본 펼침)
  const [secOpen, setSecOpen] = useState(() => ({
    fav: true, recent: true, shared: true, personal: true,
  }));
  // 나에게 공유된 page_id 집합 (page_permissions, principal_type='user' && principal_id=myUid).
  const [sharedToMe, setSharedToMe] = useState(() => new Set());

  // 내 uid 로 공유된 page_id 집합을 한 번 조회 → 메모리 매칭으로 분류에 사용.
  useEffect(() => {
    let active = true;
    if (!myUid) { setSharedToMe(new Set()); return undefined; }
    (async () => {
      try {
        const { data, error } = await supabase
          .schema('workspace')
          .from('page_permissions')
          .select('page_id')
          .eq('principal_type', 'user')
          .eq('principal_id', myUid);
        if (!active) return;
        if (error) { setSharedToMe(new Set()); return; }
        setSharedToMe(new Set((data || []).map((r) => r.page_id)));
      } catch {
        if (active) setSharedToMe(new Set());
      }
    })();
    return () => { active = false; };
  }, [myUid, pages]);
  const [menu, setMenu] = useState(null);        // { node, x, y }
  const [renaming, setRenaming] = useState(null); // { id, value }
  const renameRef = useRef(null);

  // 팀스페이스 CRUD 상태
  const [tsMenu, setTsMenu] = useState(null);        // { ts, x, y }
  const [tsForm, setTsForm] = useState(null);        // { mode:'create'|'edit', ts? }
  const [tsRenaming, setTsRenaming] = useState(null); // { id, value }
  const tsRenameRef = useRef(null);
  const [toast, setToast] = useState('');           // 가벼운 에러 알림

  // 컬렉션(커스텀 섹션) CRUD 상태
  const [colMenu, setColMenu] = useState(null);        // { col, x, y }
  const [colForm, setColForm] = useState(null);        // { mode:'create'|'edit', col? }
  const [colRenaming, setColRenaming] = useState(null); // { id, value }
  const colRenameRef = useRef(null);
  // 페이지 → 섹션 이동 서브메뉴 상태 (페이지 컨텍스트 메뉴 안)
  const [moveOpen, setMoveOpen] = useState(false);

  // 팀스페이스별 트리 (해당 팀의 최상위 페이지 + 하위 트리).
  const teamspaceTrees = useMemo(() => {
    const map = new Map();
    teamspaces.forEach((ts) => {
      map.set(ts.id, buildTree(filterPagesByTeamspace(pages, ts.id)));
    });
    return map;
  }, [pages, teamspaces]);

  // 컬렉션별 트리 (collection_id 매칭 + teamspace_id null 인 페이지).
  const collectionTrees = useMemo(() => {
    const map = new Map();
    collections.forEach((col) => {
      map.set(col.id, buildTree(filterPagesByCollection(pages, col.id)));
    });
    return map;
  }, [pages, collections]);

  // 페이지 id → 페이지 매핑 (플랫 리스트 조회용).
  const pagesById = useMemo(() => {
    const m = new Map();
    pages.forEach((p) => m.set(p.id, p));
    return m;
  }, [pages]);

  // ⭐ 즐겨찾기 — 내 즐겨찾기 page_id 를 실제 페이지로 매핑 (살아있는 페이지만, 순서 유지).
  const favoritePages = useMemo(
    () => favoriteIds.map((id) => pagesById.get(id)).filter(Boolean),
    [favoriteIds, pagesById]
  );

  // 🕓 최근 항목 — 최근 방문 page_id 를 페이지로 매핑 (상위 10, visited_at desc 순서 유지).
  const recentPages = useMemo(
    () => recentIds.map((id) => pagesById.get(id)).filter(Boolean).slice(0, 10),
    [recentIds, pagesById]
  );

  // 소속(팀스페이스) 없는 페이지를 "공유된 페이지" vs "개인 페이지" 로 분류.
  // - 개인: created_by == 나
  // - 공유: created_by != 나 && page_permissions 로 나에게 공유됨
  // 한 페이지가 한 섹션에만 들어가도록 (중복 방지).
  const { sharedTree, personalTree } = useMemo(() => {
    const unassigned = filterPagesByTeamspace(pages, null);
    const personal = [];
    const shared = [];
    unassigned.forEach((p) => {
      // 커스텀 섹션(컬렉션)에 배정된 페이지는 그 섹션에만 노출 → 개인/공유에서 제외.
      if (!hasNoCollection(p)) return;
      if (p.created_by === myUid) personal.push(p);
      else if (sharedToMe.has(p.id)) shared.push(p);
      // 그 외(내 것도 아니고 나에게 공유도 안 된 소속 없는 페이지)는 표시하지 않음.
    });
    // 트리 구성 시, 부모가 같은 섹션 집합 안에 없으면 해당 페이지가 루트로 승격됨(buildTree 동작).
    return { sharedTree: buildTree(shared), personalTree: buildTree(personal) };
  }, [pages, myUid, sharedToMe]);

  // 선택된 페이지의 조상들을 자동 펼침 (딥링크 진입 시 트리 노출).
  // routeId 가 바뀔 때만 1회 펼친다. (pages 가 rt:pages 로 재fetch 될 때마다
  //  실행되면 사용자가 수동으로 접은 조상을 계속 다시 펼치는 버그가 생김)
  const lastExpandedRoute = useRef(null);
  useEffect(() => {
    if (!routeId || pages.length === 0) return;
    if (lastExpandedRoute.current === routeId) return;
    lastExpandedRoute.current = routeId;
    const byId = new Map(pages.map((p) => [p.id, p]));
    const toOpen = new Set(expanded);
    let cur = byId.get(routeId);
    let guard = 0;
    while (cur && cur.parent_id && guard < 100) {
      toOpen.add(cur.parent_id);
      cur = byId.get(cur.parent_id);
      guard += 1;
    }
    if (toOpen.size !== expanded.size) setExpanded(toOpen);

    // 선택된 페이지가 속한 팀스페이스도 자동 펼침.
    const target = byId.get(routeId);
    if (target?.teamspace_id && !tsOpen.has(target.teamspace_id)) {
      setTsOpen((prev) => new Set(prev).add(target.teamspace_id));
    }
  }, [routeId, pages]); // eslint-disable-line

  useEffect(() => {
    if (renaming && renameRef.current) {
      renameRef.current.focus();
      renameRef.current.select();
    }
  }, [renaming]);

  useEffect(() => {
    if (tsRenaming && tsRenameRef.current) {
      tsRenameRef.current.focus();
      tsRenameRef.current.select();
    }
  }, [tsRenaming]);

  useEffect(() => {
    if (colRenaming && colRenameRef.current) {
      colRenameRef.current.focus();
      colRenameRef.current.select();
    }
  }, [colRenaming]);

  // 토스트 자동 닫힘
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // 페이지 메뉴 열려있을 때 바깥 클릭 / ESC 로 닫기
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => { setMenu(null); setMoveOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') { setMenu(null); setMoveOpen(false); } };
    window.addEventListener('click', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  // 컬렉션 메뉴/폼 열려있을 때 바깥 클릭 / ESC 로 닫기
  useEffect(() => {
    if (!colMenu && !colForm) return undefined;
    const close = () => { setColMenu(null); setColForm(null); };
    const onKey = (e) => { if (e.key === 'Escape') { setColMenu(null); setColForm(null); } };
    window.addEventListener('click', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [colMenu, colForm]);

  // 팀스페이스 메뉴/폼 열려있을 때 바깥 클릭 / ESC 로 닫기
  useEffect(() => {
    if (!tsMenu && !tsForm) return undefined;
    const close = () => { setTsMenu(null); setTsForm(null); };
    const onKey = (e) => { if (e.key === 'Escape') { setTsMenu(null); setTsForm(null); } };
    window.addEventListener('click', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [tsMenu, tsForm]);

  function toggle(id) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleTs(id) {
    setTsOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleCol(id) {
    setColOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSection(key) {
    setSecOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  async function toggleFav(node) {
    setMenu(null);
    await toggleFavorite(node.id);
  }

  async function addTeamspacePage(ts) {
    const created = await createPage({ teamspaceId: ts.id });
    if (created) {
      setTsOpen((prev) => new Set(prev).add(ts.id));
      openPage(created.id);
    }
  }

  // 이 컬렉션에 새 페이지 생성 (collection_id 설정, 팀스페이스 없음).
  async function addCollectionPage(col) {
    const created = await createPage({ collectionId: col.id });
    if (created) {
      setColOpen((prev) => new Set(prev).add(col.id));
      openPage(created.id);
    }
  }

  function goTo(path) {
    navigate(path);
    if (onNavigate) onNavigate();
  }

  function openPage(id) {
    navigate(`/page/${id}`);
    if (onNavigate) onNavigate();
  }

  async function addRootPage() {
    const created = await createPage({});
    if (created) openPage(created.id);
  }

  async function addDatabase() {
    const created = await createPage({ type: 'database' });
    if (created) openPage(created.id);
  }

  // 템플릿에서 새 페이지 (마이그레이션 0023) — 템플릿 이름/아이콘으로 페이지 생성 후
  // page_content 에 템플릿 ystate 를 복사하고 이동. TemplatePicker 의 onSelect 로 호출됨.
  async function addPageFromTemplate(tpl) {
    const created = await createPage({ title: tpl.name || '', icon: tpl.icon || null });
    if (!created) {
      setToast('템플릿에서 페이지를 만들지 못했습니다.');
      return false;
    }
    if (tpl.ystate_base64) {
      // 이동(useCollab 스냅샷 로드) 전에 본문을 먼저 넣어야 빈 문서로 열리지 않는다.
      const { error } = await supabase
        .schema('workspace')
        .from('page_content')
        .upsert(
          { page_id: created.id, ystate: tpl.ystate_base64, updated_at: new Date().toISOString() },
          { onConflict: 'page_id' }
        );
      if (error) setToast('템플릿 본문 복사에 실패했습니다. 빈 페이지로 생성되었습니다.');
    }
    try { window.dispatchEvent(new Event('rt:pages')); } catch (e) { /* noop */ }
    openPage(created.id);
    return true;
  }

  async function addChildPage(parent) {
    // 하위 페이지는 부모의 소속(팀스페이스/컬렉션)을 그대로 상속받아 같은 섹션 트리에 보이게 한다.
    const created = await createPage({
      parentId: parent.id,
      teamspaceId: parent.teamspace_id ?? null,
      collectionId: parent.collection_id ?? null,
    });
    if (created) {
      setExpanded((prev) => new Set(prev).add(parent.id));
      openPage(created.id);
    }
  }

  function openMenu(node, anchorEl) {
    const r = anchorEl.getBoundingClientRect();
    setMenu({ node, x: r.left, y: r.bottom + 4 });
  }

  function startRename(node) {
    setMenu(null);
    setRenaming({ id: node.id, value: node.title || '' });
  }

  async function commitRename() {
    if (!renaming) return;
    const { id, value } = renaming;
    setRenaming(null);
    const orig = pages.find((p) => p.id === id);
    if (orig && (orig.title || '') !== value) {
      await updatePage(id, { title: value });
    }
  }

  async function trashPage(node) {
    setMenu(null);
    await archivePage(node.id);
    if (routeId === node.id) navigate('/');
  }

  // ---------- 팀스페이스 CRUD ----------
  const tsTable = () => supabase.schema('workspace').from('teamspaces');

  function describeError(error) {
    const code = error?.code || '';
    const msg = error?.message || '';
    // RLS 권한 오류(Postgres 42501 / PostgREST 권한 코드)만 권한 문구로 안내.
    // 그 외(중복 키, 네트워크 등)는 실제 메시지를 그대로 노출해 오인 방지.
    const isPermission =
      code === '42501' ||
      /^PGRST(116|301|302)$/.test(code) ||
      /permission denied|violates row-level security|insufficient_privilege/i.test(msg);
    if (isPermission) {
      return '권한이 없습니다. 생성자 또는 관리자만 가능합니다.';
    }
    return msg || '작업에 실패했습니다.';
  }

  // 팀스페이스 생성. 성공하면 true 반환(폼 닫힘), 실패하면 false.
  async function createTeamspace({ name, icon, visibility }) {
    if (!membership?.org_id || !user?.id) {
      setToast('워크스페이스 정보를 불러오지 못했습니다.');
      return false;
    }
    const { data, error } = await tsTable()
      .insert({
        org_id: membership.org_id,
        name,
        icon: icon || '📁',
        visibility: visibility || 'open',
        created_by: user.id,
      })
      .select()
      .single();
    if (error) { setToast(describeError(error)); return false; }

    // 비공개 팀스페이스는 본인을 멤버로 추가해야 사이드바에 노출됨.
    if (visibility === 'private' && data?.id) {
      const { error: memErr } = await supabase
        .schema('workspace')
        .from('teamspace_members')
        .upsert({ teamspace_id: data.id, user_id: user.id, role: 'owner' }, { onConflict: 'teamspace_id,user_id', ignoreDuplicates: true });
      if (memErr) setToast(describeError(memErr));
    }

    await refresh();
    if (data?.id) setTsOpen((prev) => new Set(prev).add(data.id));
    setTsForm(null);
    return true;
  }

  // 팀스페이스 부분 수정 (이름/아이콘/공개범위).
  async function patchTeamspace(tsId, patch) {
    const { error } = await tsTable().update(patch).eq('id', tsId);
    if (error) { setToast(describeError(error)); return false; }
    await refresh();
    return true;
  }

  async function deleteTeamspace(ts) {
    setTsMenu(null);
    const okConfirm = window.confirm(
      `팀스페이스 "${ts.name}"을(를) 삭제할까요?\n이 작업은 되돌릴 수 없습니다.`
    );
    if (!okConfirm) return;
    const { error } = await tsTable().delete().eq('id', ts.id);
    if (error) { setToast(describeError(error)); return; }
    setTsOpen((prev) => { const n = new Set(prev); n.delete(ts.id); return n; });
    await refresh();
  }

  function openTsMenu(ts, anchorEl) {
    const r = anchorEl.getBoundingClientRect();
    setTsForm(null);
    setTsMenu({ ts, x: Math.max(8, r.left - 120), y: r.bottom + 4 });
  }

  function startTsRename(ts) {
    setTsMenu(null);
    setTsRenaming({ id: ts.id, value: ts.name || '' });
  }

  async function commitTsRename() {
    if (!tsRenaming) return;
    const { id, value } = tsRenaming;
    setTsRenaming(null);
    const orig = teamspaces.find((t) => t.id === id);
    const trimmed = value.trim();
    if (orig && trimmed && orig.name !== trimmed) {
      await patchTeamspace(id, { name: trimmed });
    }
  }

  function openTsEdit(ts) {
    setTsMenu(null);
    setTsForm({ mode: 'edit', ts });
  }

  async function cycleVisibility(ts) {
    setTsMenu(null);
    const idx = VIS_ORDER.indexOf(ts.visibility || 'open');
    const next = VIS_ORDER[(idx + 1) % VIS_ORDER.length];
    const ok = await patchTeamspace(ts.id, { visibility: next });
    // 비공개로 바꾸면 본인 멤버십이 없을 경우 노출이 끊길 수 있으므로 멤버 추가 시도.
    if (ok && next === 'private' && user?.id) {
      await supabase
        .schema('workspace')
        .from('teamspace_members')
        .upsert({ teamspace_id: ts.id, user_id: user.id, role: 'owner' }, { onConflict: 'teamspace_id,user_id', ignoreDuplicates: true });
      await refresh();
    }
  }

  // ---------- 컬렉션(커스텀 섹션) CRUD ----------
  // 컬렉션 생성. 성공 시 true(폼 닫힘).
  async function submitCreateCollection({ name, icon }) {
    const created = await createCollection({ name, icon });
    if (!created) { setToast('섹션을 만들지 못했습니다. 잠시 후 다시 시도해 주세요.'); return false; }
    setColOpen((prev) => new Set(prev).add(created.id));
    setColForm(null);
    return true;
  }

  // 컬렉션 편집(이름/아이콘). 성공 시 true.
  async function submitEditCollection(col, { name, icon }) {
    const ok = await renameCollection(col.id, { name, icon });
    if (!ok) { setToast('섹션을 수정하지 못했습니다.'); return false; }
    setColForm(null);
    return true;
  }

  function openColMenu(col, anchorEl) {
    const r = anchorEl.getBoundingClientRect();
    setColForm(null);
    setColMenu({ col, x: Math.max(8, r.left - 120), y: r.bottom + 4 });
  }

  function startColRename(col) {
    setColMenu(null);
    setColRenaming({ id: col.id, value: col.name || '' });
  }

  async function commitColRename() {
    if (!colRenaming) return;
    const { id, value } = colRenaming;
    setColRenaming(null);
    const orig = collections.find((c) => c.id === id);
    const trimmed = value.trim();
    if (orig && trimmed && orig.name !== trimmed) {
      const ok = await renameCollection(id, { name: trimmed });
      if (!ok) setToast('섹션 이름을 변경하지 못했습니다.');
    }
  }

  function openColEdit(col) {
    setColMenu(null);
    setColForm({ mode: 'edit', col });
  }

  async function removeCollection(col) {
    setColMenu(null);
    const okConfirm = window.confirm(
      `섹션 "${col.name}"을(를) 삭제할까요?\n섹션 안의 페이지는 삭제되지 않고 개인 페이지로 이동합니다.`
    );
    if (!okConfirm) return;
    const ok = await deleteCollection(col.id);
    if (!ok) { setToast('섹션을 삭제하지 못했습니다.'); return; }
    setColOpen((prev) => { const n = new Set(prev); n.delete(col.id); return n; });
  }

  // 페이지를 컬렉션으로 이동 / 섹션에서 빼기 (collectionId === null 이면 해제).
  async function movePageToCollection(node, collectionId) {
    setMenu(null);
    setMoveOpen(false);
    const ok = await updatePage(node.id, { collection_id: collectionId });
    if (!ok) { setToast('섹션 이동에 실패했습니다. collection_id 컬럼이 준비되었는지 확인해 주세요.'); return; }
    if (collectionId) setColOpen((prev) => new Set(prev).add(collectionId));
  }

  return (
    <aside className={`nav-sidebar${mobileOpen ? ' open' : ''}`} id="nav-side">
      <div className="nav-ws">
        <img src="/logo-symbol.png" alt={membership?.org_name || '루나르트'} style={{ width: 22, height: 22, objectFit: 'contain' }} />
        <span className="nm">{membership?.org_name || '워크스페이스'}</span>
        <span className="cv">⌄</span>
      </div>

      <div className="nav-scroll">
        <div
          className="nav-item"
          onClick={() => {
            // 커맨드 팔레트 열기 (App.jsx 가 rt:open-palette 수신). 모바일 드로어는 닫음.
            try { window.dispatchEvent(new Event('rt:open-palette')); } catch (e) { /* noop */ }
            if (onNavigate) onNavigate();
          }}
        >
          <span className="nav-tw spacer" />
          <span className="ic">🔍</span>
          <span className="nav-label">검색</span>
        </div>
        <div className="nav-item" onClick={() => { navigate('/'); if (onNavigate) onNavigate(); }}>
          <span className="nav-tw spacer" />
          <span className="ic">🏠</span>
          <span className="nav-label">홈</span>
        </div>
        <div className="nav-item" onClick={() => goTo('/inbox')}>
          <span className="nav-tw spacer" />
          <span className="ic">📥</span>
          <span className="nav-label">수신함</span>
        </div>

        {/* ⭐ 즐겨찾기 — 비어있으면 섹션 숨김 */}
        {favoritePages.length > 0 && (
          <>
            <SectionLabel icon="⭐" label="즐겨찾기" open={secOpen.fav} onToggle={() => toggleSection('fav')} />
            {secOpen.fav && favoritePages.map((p) => (
              <FlatPageItem key={`fav-${p.id}`} page={p} selectedId={routeId} onOpen={openPage} onMenu={openMenu} />
            ))}
          </>
        )}

        {/* 🕓 최근 항목 — 비어있으면 섹션 숨김 */}
        {recentPages.length > 0 && (
          <>
            <SectionLabel icon="🕓" label="최근 항목" open={secOpen.recent} onToggle={() => toggleSection('recent')} />
            {secOpen.recent && recentPages.map((p) => (
              <FlatPageItem key={`recent-${p.id}`} page={p} selectedId={routeId} onOpen={openPage} onMenu={openMenu} />
            ))}
          </>
        )}

        <div className="nav-seclabel nav-seclabel-row">
          <span>📁 팀스페이스</span>
          <button
            type="button"
            className="nav-seclabel-add"
            title="팀스페이스 추가"
            onClick={(e) => { e.stopPropagation(); setTsMenu(null); setTsForm({ mode: 'create' }); }}
          >＋</button>
        </div>
        {tsForm?.mode === 'create' && (
          <TeamspaceForm
            title="새 팀스페이스"
            submitLabel="추가"
            onSubmit={createTeamspace}
            onCancel={() => setTsForm(null)}
          />
        )}
        {teamspaces.length === 0 && tsForm?.mode !== 'create' && (
          <div className="nav-empty-child">팀스페이스가 없습니다</div>
        )}
        {teamspaces.map((t) => (
          tsForm?.mode === 'edit' && tsForm.ts?.id === t.id ? (
            <TeamspaceForm
              key={t.id}
              title="팀스페이스 편집"
              submitLabel="저장"
              initial={t}
              onSubmit={async (vals) => {
                const ok = await patchTeamspace(t.id, vals);
                if (ok) {
                  if (vals.visibility === 'private' && user?.id) {
                    await supabase
                      .schema('workspace')
                      .from('teamspace_members')
                      .insert({ teamspace_id: t.id, user_id: user.id, role: 'owner' });
                    await refresh();
                  }
                  setTsForm(null);
                }
                return ok;
              }}
              onCancel={() => setTsForm(null)}
            />
          ) : (
            <TeamspaceSection
              key={t.id}
              teamspace={t}
              tree={teamspaceTrees.get(t.id) || []}
              isOpen={tsOpen.has(t.id)}
              onToggle={toggleTs}
              onAddPage={addTeamspacePage}
              onTsMenu={openTsMenu}
              tsRenaming={tsRenaming}
              tsRenameRef={tsRenameRef}
              onTsRenameChange={(v) => setTsRenaming((r) => (r ? { ...r, value: v } : r))}
              onTsRenameCommit={commitTsRename}
              onTsRenameCancel={() => setTsRenaming(null)}
              selectedId={routeId}
              expanded={expanded}
              toggle={toggle}
              onOpen={openPage}
              onAddChild={addChildPage}
              onMenu={openMenu}
              renaming={renaming}
              renameRef={renameRef}
              onRenameChange={(v) => setRenaming((r) => (r ? { ...r, value: v } : r))}
              onRenameCommit={commitRename}
              onRenameCancel={() => setRenaming(null)}
            />
          )
        ))}

        {/* 🗂️ 커스텀 섹션(컬렉션) — 사용자가 직접 만든 카테고리. 팀스페이스와 공유된 페이지 사이 */}
        <div className="nav-seclabel nav-seclabel-row">
          <span>🗂️ 섹션</span>
          <button
            type="button"
            className="nav-seclabel-add"
            title="새 섹션"
            onClick={(e) => { e.stopPropagation(); setColMenu(null); setColForm({ mode: 'create' }); }}
          >＋</button>
        </div>
        {colForm?.mode === 'create' && (
          <CollectionForm
            title="새 섹션"
            submitLabel="추가"
            onSubmit={submitCreateCollection}
            onCancel={() => setColForm(null)}
          />
        )}
        {collections.length === 0 && colForm?.mode !== 'create' && (
          <div className="nav-empty-child">섹션이 없습니다</div>
        )}
        {collections.map((c) => (
          colForm?.mode === 'edit' && colForm.col?.id === c.id ? (
            <CollectionForm
              key={c.id}
              title="섹션 편집"
              submitLabel="저장"
              initial={c}
              onSubmit={(vals) => submitEditCollection(c, vals)}
              onCancel={() => setColForm(null)}
            />
          ) : (
            <CollectionSection
              key={c.id}
              collection={c}
              tree={collectionTrees.get(c.id) || []}
              isOpen={colOpen.has(c.id)}
              onToggle={toggleCol}
              onAddPage={addCollectionPage}
              onColMenu={openColMenu}
              colRenaming={colRenaming}
              colRenameRef={colRenameRef}
              onColRenameChange={(v) => setColRenaming((r) => (r ? { ...r, value: v } : r))}
              onColRenameCommit={commitColRename}
              onColRenameCancel={() => setColRenaming(null)}
              selectedId={routeId}
              expanded={expanded}
              toggle={toggle}
              onOpen={openPage}
              onAddChild={addChildPage}
              onMenu={openMenu}
              renaming={renaming}
              renameRef={renameRef}
              onRenameChange={(v) => setRenaming((r) => (r ? { ...r, value: v } : r))}
              onRenameCommit={commitRename}
              onRenameCancel={() => setRenaming(null)}
            />
          )
        ))}

        {/* 🤝 공유된 페이지 — 내가 만들지 않았지만 나에게 공유된 소속 없는 페이지. 비어있으면 숨김 */}
        {!loading && sharedTree.length > 0 && (
          <>
            <SectionLabel icon="🤝" label="공유된 페이지" open={secOpen.shared} onToggle={() => toggleSection('shared')} />
            {secOpen.shared && sharedTree.map((node) => (
              <PageNode
                key={`shared-${node.id}`}
                node={node}
                depth={0}
                selectedId={routeId}
                expanded={expanded}
                toggle={toggle}
                onOpen={openPage}
                onAddChild={addChildPage}
                onMenu={openMenu}
                renaming={renaming}
                renameRef={renameRef}
                onRenameChange={(v) => setRenaming((r) => (r ? { ...r, value: v } : r))}
                onRenameCommit={commitRename}
                onRenameCancel={() => setRenaming(null)}
              />
            ))}
          </>
        )}

        {/* 📄 개인 페이지 — created_by == 나 && teamspace_id is null */}
        <SectionLabel icon="📄" label="개인 페이지" open={secOpen.personal} onToggle={() => toggleSection('personal')} />
        {loading && <div className="nav-loading">불러오는 중…</div>}
        {!loading && secOpen.personal && personalTree.length === 0 && (
          <div className="nav-empty-child">아직 페이지가 없습니다</div>
        )}
        {!loading && secOpen.personal &&
          personalTree.map((node) => (
            <PageNode
              key={node.id}
              node={node}
              depth={0}
              selectedId={routeId}
              expanded={expanded}
              toggle={toggle}
              onOpen={openPage}
              onAddChild={addChildPage}
              onMenu={openMenu}
              renaming={renaming}
              renameRef={renameRef}
              onRenameChange={(v) => setRenaming((r) => (r ? { ...r, value: v } : r))}
              onRenameCommit={commitRename}
              onRenameCancel={() => setRenaming(null)}
            />
          ))}

        <div className="nav-item nav-newpage" onClick={addRootPage}>
          <span className="nav-tw spacer" />
          <span className="ic">＋</span>
          <span className="nav-label">새 페이지</span>
        </div>
        <div className="nav-item nav-newpage" onClick={addDatabase}>
          <span className="nav-tw spacer" />
          <span className="ic">＋</span>
          <span className="nav-label">새 데이터베이스</span>
        </div>
        {/* 📑 템플릿에서 새 페이지 (마이그레이션 0023) */}
        <TemplatePicker onSelect={addPageFromTemplate} />
      </div>

      <div className="nav-foot">
        <div className="nav-item" onClick={() => goTo('/import')}>
          <span className="nav-tw spacer" />
          <span className="ic">📥</span>
          <span className="nav-label">노션에서 가져오기</span>
        </div>
        <div className="nav-item" onClick={() => goTo('/admin/members')}>
          <span className="nav-tw spacer" />
          <span className="ic">⚙️</span>
          <span className="nav-label">설정 · 멤버</span>
        </div>
        <div className="nav-item" onClick={() => goTo('/trash')}>
          <span className="nav-tw spacer" />
          <span className="ic">🗑️</span>
          <span className="nav-label">휴지통</span>
        </div>
        <div className="nav-item" onClick={signOut}>
          <span className="nav-tw spacer" />
          <span className="ic">↩︎</span>
          <span className="nav-label">로그아웃</span>
        </div>
      </div>

      {menu && (
        <div className="nav-menu" style={{ left: menu.x, top: menu.y }} onClick={(e) => e.stopPropagation()}>
          <button type="button" className="mi" onClick={() => toggleFav(menu.node)}>
            {isFavorite(menu.node.id) ? '☆ 즐겨찾기 해제' : '⭐ 즐겨찾기 추가'}
          </button>
          <button type="button" className="mi" onClick={() => startRename(menu.node)}>
            ✏️ 이름 변경
          </button>
          {/* 섹션으로 이동 — 팀스페이스에 속한 페이지는 컬렉션 이동 대상이 아니므로 숨김 */}
          {!menu.node.teamspace_id && (
            <div
              className="nav-submenu-wrap"
              onMouseEnter={() => setMoveOpen(true)}
              onMouseLeave={() => setMoveOpen(false)}
            >
              <button
                type="button"
                className="mi mi-submenu"
                onClick={(e) => { e.stopPropagation(); setMoveOpen((v) => !v); }}
              >
                <span>📂 섹션으로 이동</span>
                <span className="mi-arrow">▸</span>
              </button>
              {moveOpen && (
                <div className="nav-submenu" onClick={(e) => e.stopPropagation()}>
                  {collections.length === 0 && (
                    <div className="nav-submenu-empty">섹션이 없습니다</div>
                  )}
                  {collections.map((c) => {
                    const isCurrent = menu.node.collection_id === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        className="mi"
                        disabled={isCurrent}
                        onClick={() => movePageToCollection(menu.node, c.id)}
                      >
                        <span className="mi-ic">{c.icon || '📁'}</span>
                        {c.name}{isCurrent ? ' ✓' : ''}
                      </button>
                    );
                  })}
                  {!hasNoCollection(menu.node) && (
                    <button
                      type="button"
                      className="mi mi-move-out"
                      onClick={() => movePageToCollection(menu.node, null)}
                    >
                      ↩︎ 섹션에서 빼기
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          <button type="button" className="mi danger" onClick={() => trashPage(menu.node)}>
            🗑️ 휴지통
          </button>
        </div>
      )}

      {colMenu && (
        <div className="nav-menu" style={{ left: colMenu.x, top: colMenu.y }} onClick={(e) => e.stopPropagation()}>
          <button type="button" className="mi" onClick={() => startColRename(colMenu.col)}>
            ✏️ 이름 변경
          </button>
          <button type="button" className="mi" onClick={() => openColEdit(colMenu.col)}>
            🎨 아이콘 편집
          </button>
          <button type="button" className="mi danger" onClick={() => removeCollection(colMenu.col)}>
            🗑️ 섹션 삭제
          </button>
        </div>
      )}

      {tsMenu && (
        <div className="nav-menu" style={{ left: tsMenu.x, top: tsMenu.y }} onClick={(e) => e.stopPropagation()}>
          <button type="button" className="mi" onClick={() => startTsRename(tsMenu.ts)}>
            ✏️ 이름 변경
          </button>
          <button type="button" className="mi" onClick={() => openTsEdit(tsMenu.ts)}>
            🎨 아이콘 · 공개범위 편집
          </button>
          <button type="button" className="mi" onClick={() => cycleVisibility(tsMenu.ts)}>
            🔁 공개범위 전환 ({VISIBILITY[tsMenu.ts.visibility]?.label || '공개'})
          </button>
          <button type="button" className="mi danger" onClick={() => deleteTeamspace(tsMenu.ts)}>
            🗑️ 팀스페이스 삭제
          </button>
        </div>
      )}

      {toast && <div className="nav-toast">{toast}</div>}

      {/* 백링크 패널 — 'rt:open-backlinks' 이벤트 수신용 상시 마운트 (portal 로 body 에 렌더) */}
      <BacklinksPanel />
    </aside>
  );
}
